import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";
import type { AdminUpdateExpertClaimRequest } from "@shopexperts/legacy-api";
import { extractServerMessage, extractStatus } from "@/app/composables/claim/claimApiErrors";

export function useExpertClaimActions() {
  const api = useApi();
  const queryClient = useQueryClient();
  const toaster = useToaster();

  const invalidateClaims = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "expert-claims"] });
  };

  // Prefer the server's message verbatim — the claims API returns precise 409
  // guard messages ("This claim has already been decided.", "A decided claim
  // cannot be edited. Cancel the decision first.", …).
  const handleActionError = (error: unknown, fallback: string) => {
    toaster.create({ title: extractServerMessage(error) || fallback, type: "error" });
    // A 409 usually means the list is stale (another admin acted on the claim
    // meanwhile) — refetch so the UI reflects the real state.
    invalidateClaims();
  };

  const approve = useMutation({
    mutationFn: async ({ claimId, adminNote }: { claimId: number; adminNote?: string }) => {
      return await api.admin.expertClaims.approve({
        params: { claimId },
        body: adminNote ? { admin_note: adminNote } : {},
      });
    },
    onSuccess: (result) => {
      const email = result.claim.created_user?.email ?? result.claim.email;
      toaster.create({
        title: "Claim approved",
        description: result.user_was_existing
          ? `Linked existing account ${email}. Any other undecided claims for this profile were auto-rejected.`
          : `Created account ${email} and sent a create-password email. Any other undecided claims for this profile were auto-rejected.`,
        type: "success",
      });
      invalidateClaims();
    },
    onError: (error: Error) => handleActionError(error, "Failed to approve claim"),
  });

  const reject = useMutation({
    mutationFn: async ({ claimId, adminNote }: { claimId: number; adminNote?: string }) => {
      return await api.admin.expertClaims.reject({
        params: { claimId },
        body: adminNote ? { admin_note: adminNote } : {},
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Claim rejected", type: "success" });
      invalidateClaims();
    },
    onError: (error: Error) => handleActionError(error, "Failed to reject claim"),
  });

  const cancelDecision = useMutation({
    mutationFn: async ({ claimId }: { claimId: number }) => {
      return await api.admin.expertClaims.cancelDecision({
        params: { claimId },
      });
    },
    onSuccess: () => {
      toaster.create({
        title: "Decision cancelled",
        description: "The claim is open for review again.",
        type: "success",
      });
      invalidateClaims();
    },
    onError: (error: Error) => handleActionError(error, "Failed to cancel decision"),
  });

  const update = useMutation({
    mutationFn: async ({ claimId, changes }: {
      claimId: number;
      changes: AdminUpdateExpertClaimRequest;
      /**
       * True when the email changed case-insensitively — the API's rule for
       * resetting verification and re-sending the email. A casing-only edit
       * still persists (so it is sent), but triggers no reset. The edit
       * dialog computes this with the same helper that drives its warning.
       */
      emailChanged?: boolean;
    }) => {
      return await api.admin.expertClaims.update({
        params: { claimId },
        body: changes,
      });
    },
    onSuccess: (_result, variables) => {
      toaster.create({
        title: "Claim updated",
        description: variables.emailChanged
          ? "The claim was reset to unverified and a verification email was sent to the new address."
          : undefined,
        type: "success",
      });
      invalidateClaims();
    },
    onError: (error: Error) => {
      // 422s render as field errors inside the edit form — no toast.
      if (extractStatus(error) === 422) return;
      handleActionError(error, "Failed to update claim");
    },
  });

  const updateNote = useMutation({
    mutationFn: async ({ claimId, adminNote }: { claimId: number; adminNote: string | null }) => {
      return await api.admin.expertClaims.updateNote({
        params: { claimId },
        body: { admin_note: adminNote },
      });
    },
    onSuccess: (_result, variables) => {
      toaster.create({
        title: variables.adminNote === null ? "Note cleared" : "Note saved",
        type: "success",
      });
      invalidateClaims();
    },
    onError: (error: Error) => handleActionError(error, "Failed to save note"),
  });

  return {
    approve,
    reject,
    cancelDecision,
    update,
    updateNote,
  };
}
